import type { DisplayType } from '@/types/display-types';
import type { Component } from '@/types/components';
import type { DisplayComponent } from '@/types/display-components';
import type { ComponentInventory } from '@/types/component-inventory';
import type { ScreenHistory } from '@/types/screen-history';
import type { LocationOrder, LocationOrderItem } from '@/types/location-orders';

// Mock Display Types
export const mockDisplayTypes: DisplayType[] = [
  // 22" Flower Stations - 3 SKU variants
  {
    id: 'dt-2-8',
    name: 'Flower Station',
    description: 'Interactive flower display station',
    size: '22"',
    category: 'flower_station',
    selling_price: 1200.0,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dt-2-16',
    name: 'Flower Station',
    description: 'Interactive flower display station',
    size: '22"',
    category: 'flower_station',
    selling_price: 1400.0,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dt-2-20',
    name: 'Flower Station',
    description: 'Interactive flower display station',
    size: '22"',
    category: 'flower_station',
    selling_price: 1600.0,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  // 27" Flower Stations - 3 SKU variants
  {
    id: 'dt-1-8',
    name: 'Flower Station',
    description: 'Interactive flower display station',
    size: '27"',
    category: 'flower_station',
    selling_price: 1300.0,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dt-1-16',
    name: 'Flower Station',
    description: 'Interactive flower display station',
    size: '27"',
    category: 'flower_station',
    selling_price: 1500.0,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dt-1-20',
    name: 'Flower Station',
    description: 'Interactive flower display station',
    size: '27"',
    category: 'flower_station',
    selling_price: 1700.0,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dt-3',
    name: 'Category Station',
    description: 'Category browsing station',
    size: '27"',
    category: 'category_station',
    selling_price: 1100.0,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dt-4',
    name: 'Category Station',
    description: 'Category browsing station',
    size: '21.5"',
    category: 'category_station',
    selling_price: 1000.0,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dt-5',
    name: 'Quick Checkout Station',
    description: 'Quick checkout kiosk',
    size: null,
    category: 'quick_checkout',
    selling_price: 800.0,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
];

// Mock Components
export const mockComponents: Component[] = [
  {
    id: 'comp-1',
    name: '27" Estone Screen',
    sku: 'ESTONE-27',
    component_type: 'screen',
    default_vendor: 'Estone',
    default_cost: 450.0,
    description: '27" touchscreen display from Estone',
    has_serial_number: true,
    warranty_period_days: 730, // 2 years
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'comp-2',
    name: '21.5" Estone Screen',
    sku: 'ESTONE-21.5',
    component_type: 'screen',
    default_vendor: 'Estone',
    default_cost: 350.0,
    description: '21.5" touchscreen display from Estone',
    has_serial_number: true,
    warranty_period_days: 730, // 2 years
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'comp-3',
    name: 'Mini PC Computer',
    sku: 'MINIPC-001',
    component_type: 'computer',
    default_vendor: 'Amazon',
    default_cost: 300.0,
    description: 'Mini PC for kiosk operation',
    has_serial_number: true,
    warranty_period_days: 365, // 1 year
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'comp-4',
    name: 'NFC Reader',
    sku: 'NFC-001',
    component_type: 'nfc_reader',
    default_vendor: 'Vendor A',
    default_cost: 75.0,
    description: 'NFC card reader',
    has_serial_number: false,
    warranty_period_days: 365,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'comp-5',
    name: 'USB Hub',
    sku: 'USBHUB-001',
    component_type: 'usb_hub',
    default_vendor: 'Vendor B',
    default_cost: 25.0,
    description: 'USB hub for peripherals',
    has_serial_number: false,
    warranty_period_days: 365,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'comp-6',
    name: 'VESA Mount',
    sku: 'MOUNT-001',
    component_type: 'mount',
    default_vendor: 'Vendor C',
    default_cost: 50.0,
    description: 'VESA mount for screen',
    has_serial_number: false,
    warranty_period_days: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
];

// Mock Display Components (BOM)
export const mockDisplayComponents: DisplayComponent[] = [
  // BOM for new 22" Flower Station variants (8, 16, 20 SKU)
  // dt-2-8, dt-2-16, dt-2-20 all use the same BOM as dt-2
  {
    id: 'dc-11',
    display_type_id: 'dt-2-8',
    component_id: 'comp-2', // 22" Estone Screen
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-12',
    display_type_id: 'dt-2-8',
    component_id: 'comp-3',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-13',
    display_type_id: 'dt-2-8',
    component_id: 'comp-4',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-14',
    display_type_id: 'dt-2-8',
    component_id: 'comp-5',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-15',
    display_type_id: 'dt-2-8',
    component_id: 'comp-6',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  // dt-2-16
  {
    id: 'dc-16',
    display_type_id: 'dt-2-16',
    component_id: 'comp-2',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-17',
    display_type_id: 'dt-2-16',
    component_id: 'comp-3',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-18',
    display_type_id: 'dt-2-16',
    component_id: 'comp-4',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-19',
    display_type_id: 'dt-2-16',
    component_id: 'comp-5',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-20',
    display_type_id: 'dt-2-16',
    component_id: 'comp-6',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  // dt-2-20
  {
    id: 'dc-21',
    display_type_id: 'dt-2-20',
    component_id: 'comp-2',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-22',
    display_type_id: 'dt-2-20',
    component_id: 'comp-3',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-23',
    display_type_id: 'dt-2-20',
    component_id: 'comp-4',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-24',
    display_type_id: 'dt-2-20',
    component_id: 'comp-5',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-25',
    display_type_id: 'dt-2-20',
    component_id: 'comp-6',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  // BOM for 27" Flower Station variants (8, 16, 20 SKU)
  // dt-1-8
  {
    id: 'dc-26',
    display_type_id: 'dt-1-8',
    component_id: 'comp-1', // 27" Estone Screen
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-27',
    display_type_id: 'dt-1-8',
    component_id: 'comp-3',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-28',
    display_type_id: 'dt-1-8',
    component_id: 'comp-4',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-29',
    display_type_id: 'dt-1-8',
    component_id: 'comp-5',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-30',
    display_type_id: 'dt-1-8',
    component_id: 'comp-6',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  // dt-1-16
  {
    id: 'dc-31',
    display_type_id: 'dt-1-16',
    component_id: 'comp-1',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-32',
    display_type_id: 'dt-1-16',
    component_id: 'comp-3',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-33',
    display_type_id: 'dt-1-16',
    component_id: 'comp-4',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-34',
    display_type_id: 'dt-1-16',
    component_id: 'comp-5',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-35',
    display_type_id: 'dt-1-16',
    component_id: 'comp-6',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  // dt-1-20
  {
    id: 'dc-36',
    display_type_id: 'dt-1-20',
    component_id: 'comp-1',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-37',
    display_type_id: 'dt-1-20',
    component_id: 'comp-3',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-38',
    display_type_id: 'dt-1-20',
    component_id: 'comp-4',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-39',
    display_type_id: 'dt-1-20',
    component_id: 'comp-5',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-40',
    display_type_id: 'dt-1-20',
    component_id: 'comp-6',
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  // BOM for Category Station 27" (dt-3)
  {
    id: 'dc-41',
    display_type_id: 'dt-3',
    component_id: 'comp-1', // 27" Estone Screen
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-42',
    display_type_id: 'dt-3',
    component_id: 'comp-3', // Mini PC Computer
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-43',
    display_type_id: 'dt-3',
    component_id: 'comp-4', // NFC Reader
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-44',
    display_type_id: 'dt-3',
    component_id: 'comp-5', // USB Hub
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'dc-45',
    display_type_id: 'dt-3',
    component_id: 'comp-6', // VESA Mount
    quantity_required: 1,
    notes: null,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
];

// Mock Component Inventory
export const mockComponentInventory: ComponentInventory[] = [
  // Screens in reserve
  {
    id: 'ci-1',
    component_id: 'comp-1', // 27" Estone Screen
    serial_number: 'ESTONE-27-001',
    purchase_date: '2024-10-15',
    purchase_cost: 450.0,
    vendor: 'Estone',
    purchase_order_id: 'po-1',
    reserve_location: 'Office Warehouse',
    quantity: 1,
    received_date: '2024-10-20',
    assignment_status: 'received',
    assigned_order_id: null,
    assigned_location_id: null,
    ship_date: null,
    installed_date: null,
    warranty_start_date: null,
    warranty_period_days: 730,
    warranty_expiration_date: null,
    is_refurbished: false,
    notes: null,
    created_at: '2024-10-15T00:00:00Z',
    updated_at: '2024-10-20T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  {
    id: 'ci-2',
    component_id: 'comp-1',
    serial_number: 'ESTONE-27-002',
    purchase_date: '2024-11-01',
    purchase_cost: 450.0,
    vendor: 'Estone',
    purchase_order_id: 'po-2',
    reserve_location: 'Office Warehouse',
    quantity: 1,
    received_date: '2024-11-05',
    assignment_status: 'received',
    assigned_order_id: null,
    assigned_location_id: null,
    ship_date: null,
    installed_date: null,
    warranty_start_date: null,
    warranty_period_days: 730,
    warranty_expiration_date: null,
    is_refurbished: false,
    notes: null,
    created_at: '2024-11-01T00:00:00Z',
    updated_at: '2024-11-05T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  // Screens installed at location 1 (Denver Downtown)
  // Flower Station screens (22" - using comp-2 as closest match)
  {
    id: 'ci-3',
    component_id: 'comp-2', // 22" Estone Screen (using 21.5" as closest)
    serial_number: 'ESTONE-22-001',
    purchase_date: '2024-01-15',
    purchase_cost: 350.0,
    vendor: 'Estone',
    purchase_order_id: null,
    reserve_location: null,
    quantity: 1,
    received_date: '2024-01-20',
    assignment_status: 'installed',
    assigned_order_id: null,
    assigned_location_id: '1', // Denver Downtown
    ship_date: '2024-02-05',
    installed_date: '2024-02-15',
    warranty_start_date: '2024-02-05',
    warranty_period_days: 730,
    warranty_expiration_date: '2026-02-05',
    is_refurbished: false,
    notes: null,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-02-15T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  {
    id: 'ci-4',
    component_id: 'comp-2',
    serial_number: 'ESTONE-22-002',
    purchase_date: '2024-01-15',
    purchase_cost: 350.0,
    vendor: 'Estone',
    purchase_order_id: null,
    reserve_location: null,
    quantity: 1,
    received_date: '2024-01-20',
    assignment_status: 'installed',
    assigned_order_id: null,
    assigned_location_id: '1',
    ship_date: '2024-02-05',
    installed_date: '2024-02-16',
    warranty_start_date: '2024-02-05',
    warranty_period_days: 730,
    warranty_expiration_date: '2023-12-31', // Expired warranty
    is_refurbished: true, // Refurbished unit
    notes: null,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-02-16T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  {
    id: 'ci-5',
    component_id: 'comp-2',
    serial_number: 'ESTONE-22-003',
    purchase_date: '2024-01-15',
    purchase_cost: 350.0,
    vendor: 'Estone',
    purchase_order_id: null,
    reserve_location: null,
    quantity: 1,
    received_date: '2024-01-20',
    assignment_status: 'installed',
    assigned_order_id: null,
    assigned_location_id: '1',
    ship_date: '2024-02-05',
    installed_date: '2024-02-17',
    warranty_start_date: '2024-02-05',
    warranty_period_days: 730,
    warranty_expiration_date: '2023-11-15', // Expired warranty
    is_refurbished: false,
    notes: null,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-02-17T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  {
    id: 'ci-6',
    component_id: 'comp-2',
    serial_number: 'ESTONE-22-004',
    purchase_date: '2024-01-15',
    purchase_cost: 350.0,
    vendor: 'Estone',
    purchase_order_id: null,
    reserve_location: null,
    quantity: 1,
    received_date: '2024-01-20',
    assignment_status: 'installed',
    assigned_order_id: null,
    assigned_location_id: '1',
    ship_date: '2024-02-05',
    installed_date: '2024-02-18',
    warranty_start_date: '2024-02-05',
    warranty_period_days: 730,
    warranty_expiration_date: '2026-02-05',
    is_refurbished: false,
    notes: null,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-02-18T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  // Category Station screens (27")
  {
    id: 'ci-7',
    component_id: 'comp-1', // 27" Estone Screen
    serial_number: 'ESTONE-27-001',
    purchase_date: '2024-01-15',
    purchase_cost: 450.0,
    vendor: 'Estone',
    purchase_order_id: null,
    reserve_location: null,
    quantity: 1,
    received_date: '2024-01-20',
    assignment_status: 'installed',
    assigned_order_id: null,
    assigned_location_id: '1',
    ship_date: '2024-02-10',
    installed_date: '2024-02-20',
    warranty_start_date: '2024-02-10',
    warranty_period_days: 730,
    warranty_expiration_date: '2026-02-10',
    is_refurbished: false,
    notes: null,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-02-20T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  {
    id: 'ci-8',
    component_id: 'comp-1',
    serial_number: 'ESTONE-27-002',
    purchase_date: '2024-01-15',
    purchase_cost: 450.0,
    vendor: 'Estone',
    purchase_order_id: null,
    reserve_location: null,
    quantity: 1,
    received_date: '2024-01-20',
    assignment_status: 'installed',
    assigned_order_id: null,
    assigned_location_id: '1',
    ship_date: '2024-02-10',
    installed_date: '2024-02-21',
    warranty_start_date: '2024-02-10',
    warranty_period_days: 730,
    warranty_expiration_date: '2023-10-20', // Expired warranty
    is_refurbished: true, // Refurbished unit
    notes: null,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-02-21T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  {
    id: 'ci-9',
    component_id: 'comp-1',
    serial_number: 'ESTONE-27-003',
    purchase_date: '2024-01-15',
    purchase_cost: 450.0,
    vendor: 'Estone',
    purchase_order_id: null,
    reserve_location: null,
    quantity: 1,
    received_date: '2024-01-20',
    assignment_status: 'installed',
    assigned_order_id: null,
    assigned_location_id: '1',
    ship_date: '2024-02-10',
    installed_date: '2024-02-22',
    warranty_start_date: '2024-02-10',
    warranty_period_days: 730,
    warranty_expiration_date: '2026-02-10',
    is_refurbished: false,
    notes: null,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-02-22T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  {
    id: 'ci-10',
    component_id: 'comp-1',
    serial_number: 'ESTONE-27-004',
    purchase_date: '2024-01-15',
    purchase_cost: 450.0,
    vendor: 'Estone',
    purchase_order_id: null,
    reserve_location: null,
    quantity: 1,
    received_date: '2024-01-20',
    assignment_status: 'installed',
    assigned_order_id: null,
    assigned_location_id: '1',
    ship_date: '2024-02-10',
    installed_date: '2024-02-23',
    warranty_start_date: '2024-02-10',
    warranty_period_days: 730,
    warranty_expiration_date: '2026-02-10',
    is_refurbished: false,
    notes: null,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-02-23T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  {
    id: 'ci-11',
    component_id: 'comp-1',
    serial_number: 'ESTONE-27-005',
    purchase_date: '2024-01-15',
    purchase_cost: 450.0,
    vendor: 'Estone',
    purchase_order_id: null,
    reserve_location: null,
    quantity: 1,
    received_date: '2024-01-20',
    assignment_status: 'installed',
    assigned_order_id: null,
    assigned_location_id: '1',
    ship_date: '2024-02-10',
    installed_date: '2024-02-24',
    warranty_start_date: '2024-02-10',
    warranty_period_days: 730,
    warranty_expiration_date: '2026-02-10',
    is_refurbished: false,
    notes: null,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-02-24T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  {
    id: 'ci-12',
    component_id: 'comp-1',
    serial_number: 'ESTONE-27-006',
    purchase_date: '2024-01-15',
    purchase_cost: 450.0,
    vendor: 'Estone',
    purchase_order_id: null,
    reserve_location: null,
    quantity: 1,
    received_date: '2024-01-20',
    assignment_status: 'installed',
    assigned_order_id: null,
    assigned_location_id: '1',
    ship_date: '2024-02-10',
    installed_date: '2024-02-25',
    warranty_start_date: '2024-02-10',
    warranty_period_days: 730,
    warranty_expiration_date: '2026-02-10',
    is_refurbished: false,
    notes: null,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-02-25T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  // Computer in reserve
  {
    id: 'ci-14',
    component_id: 'comp-3', // Mini PC
    serial_number: 'PC-001',
    purchase_date: '2024-10-15',
    purchase_cost: 300.0,
    vendor: 'Amazon',
    purchase_order_id: 'po-1',
    reserve_location: 'Office Warehouse',
    quantity: 1,
    received_date: '2024-10-18',
    assignment_status: 'received',
    assigned_order_id: null,
    assigned_location_id: null,
    ship_date: null,
    installed_date: null,
    warranty_start_date: '2024-10-15', // Starts on purchase for computers
    warranty_period_days: 365,
    warranty_expiration_date: '2025-10-15',
    is_refurbished: false,
    notes: null,
    created_at: '2024-10-15T00:00:00Z',
    updated_at: '2024-10-18T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
  // NFC Readers (non-serialized, quantity-based)
  {
    id: 'ci-16',
    component_id: 'comp-4', // NFC Reader
    serial_number: null,
    purchase_date: '2024-10-15',
    purchase_cost: 75.0,
    vendor: 'Vendor A',
    purchase_order_id: 'po-1',
    reserve_location: 'Office Warehouse',
    quantity: 5,
    received_date: '2024-10-18',
    assignment_status: 'received',
    assigned_order_id: null,
    assigned_location_id: null,
    ship_date: null,
    installed_date: null,
    warranty_start_date: null,
    warranty_period_days: 365,
    warranty_expiration_date: null,
    is_refurbished: false,
    notes: null,
    created_at: '2024-10-15T00:00:00Z',
    updated_at: '2024-10-18T00:00:00Z',
    created_by: null,
    updated_by: null,
  },
];

// Mock Screen History
export const mockScreenHistory: ScreenHistory[] = [
  {
    id: 'sh-1',
    component_inventory_id: 'ci-7', // ESTONE-27-001 (Category Station #1)
    event_type: 'purchase',
    from_location_id: null,
    to_location_id: null,
    event_date: '2024-01-15',
    vendor: 'Estone',
    cost: 450.0,
    repair_type: null,
    notes: 'Initial purchase',
    created_at: '2024-01-15T00:00:00Z',
    created_by: null,
  },
  {
    id: 'sh-2',
    component_inventory_id: 'ci-7',
    event_type: 'warranty_start',
    from_location_id: null,
    to_location_id: '1',
    event_date: '2024-02-10',
    vendor: null,
    cost: null,
    repair_type: null,
    notes: 'Shipped to Denver Downtown',
    created_at: '2024-02-10T00:00:00Z',
    created_by: null,
  },
  {
    id: 'sh-3',
    component_inventory_id: 'ci-7',
    event_type: 'install',
    from_location_id: null,
    to_location_id: '1',
    event_date: '2024-02-20',
    vendor: null,
    cost: null,
    repair_type: null,
    notes: 'Installed at Denver Downtown',
    created_at: '2024-02-20T00:00:00Z',
    created_by: null,
  },
];

// Mock Location Orders
export const mockLocationOrders: LocationOrder[] = [
  {
    id: 'lo-1',
    location_id: '1', // Denver Downtown
    deal_id: null,
    launch_id: null,
    order_date: '2024-01-15',
    status: 'paid',
    total_amount: 9000.0,
    paid_amount: 9000.0,
    paid_date: '2024-01-15',
    notes: 'Order for 4x Flower Station 22" and 6x Category Station 27"',
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-01-15T00:00:00Z',
    created_by: null,
  },
  {
    id: 'lo-2',
    location_id: '2', // Boulder Main Street
    deal_id: null,
    launch_id: null,
    order_date: '2024-10-01',
    status: 'paid',
    total_amount: 1800.0,
    paid_amount: 1800.0,
    paid_date: '2024-10-01',
    notes: 'Order for 2x Flower Station 27"',
    created_at: '2024-10-01T00:00:00Z',
    updated_at: '2024-10-01T00:00:00Z',
    created_by: null,
  },
  {
    id: 'lo-3',
    location_id: '3', // Fort Collins Campus
    deal_id: null,
    launch_id: null,
    order_date: '2024-11-01',
    status: 'paid',
    total_amount: 900.0,
    paid_amount: 900.0,
    paid_date: '2024-11-01',
    notes: 'Order for 1x Flower Station 27"',
    created_at: '2024-11-01T00:00:00Z',
    updated_at: '2024-11-01T00:00:00Z',
    created_by: null,
  },
];

// Mock Location Order Items
export const mockLocationOrderItems: LocationOrderItem[] = [
  {
    id: 'loi-1',
    order_id: 'lo-1', // Denver Downtown order
    display_type_id: 'dt-2-16', // Flower Station 22" (16-SKU)
    quantity: 4,
    unit_price: 900.0,
    created_at: '2024-01-15T00:00:00Z',
  },
  {
    id: 'loi-2',
    order_id: 'lo-1', // Denver Downtown order
    display_type_id: 'dt-3', // Category Station 27"
    quantity: 6,
    unit_price: 900.0,
    created_at: '2024-01-15T00:00:00Z',
  },
  {
    id: 'loi-3',
    order_id: 'lo-2', // Boulder Main Street order
    display_type_id: 'dt-1-16', // Flower Station 27" (16-SKU)
    quantity: 2,
    unit_price: 900.0,
    created_at: '2024-10-01T00:00:00Z',
  },
  {
    id: 'loi-4',
    order_id: 'lo-3', // Fort Collins Campus order
    display_type_id: 'dt-1-16', // Flower Station 27" (16-SKU)
    quantity: 1,
    unit_price: 900.0,
    created_at: '2024-11-01T00:00:00Z',
  },
];
